/**
 * activePillClassifier (PR-E7).
 *
 * Variant classifier for the in-game Pokémon GO Pokédex filter pill row,
 * driven by *active-pill geometry* rather than full-strip OCR.
 *
 * Why this replaces the build-2/3 pillText approach (`classifyVariantChip` in
 * pokedexOcr.ts):
 *
 *   The original classifier OCR'd the entire bottom band (FILTER_PILL_ROW_Y..H
 *   spanning x: 0..1) and ran a list of variant regexes in declaration order.
 *   The PoGo Pokédex bottom band always shows three pill labels at once
 *   (e.g. "POKEMON SHINY LUCKY"), so the regex list always saw every token.
 *   The first-match-wins rule meant LUCKY always won (it sat first in the
 *   list), and a TestFlight Main-dex scan wrote 53 cells under
 *   `variant: "lucky"` to D1. This is a +97% accuracy blocker — the user's
 *   non-negotiable contract.
 *
 *   The on-screen pill row gives us much stronger signals than text alone:
 *
 *     1. The ACTIVE pill is a solid, filled, theme-coloured capsule. Inactive
 *        pills are light-background outlined chips with grey/dark text. The
 *        active pill therefore has higher saturation AND a lower value
 *        (darker fill) than its neighbours.
 *
 *     2. The pill order in PoGo is fixed and cosmetic (verified against
 *        device screenshots IMG_0514–0525):
 *
 *           POKÉMON, SHINY, LUCKY, XXL, XXS, GMAX, MEGA, SHADOW,
 *           PURIFIED, PERFECT(100%)
 *
 *        Scrolling moves which three pills are on screen but never changes
 *        the order. POKÉMON only appears at left/middle slots (it is the
 *        leftmost in the canonical order). PERFECT (rendered as "100%")
 *        only appears at middle/right (scroll stops at PERFECT on the right
 *        end).
 *
 *     3. Each variant has a theme hue. The whole grid + header tints to
 *        match the active pill. Lucky → pink/orange (~330-360 + 0-45),
 *        Shiny → gold/yellow (~30-65), POKÉMON main → cyan/teal (~180-220),
 *        Perfect → near-grey (low saturation).
 *
 *   So we classify by GEOMETRY first (find the saturated dark pill in the
 *   band, fix three slot crops around it), OCR each slot, then pin the
 *   active variant by its slot position + neighbours against the canonical
 *   order. Theme hue cross-checks the choice. When the active pill cannot be
 *   located confidently (PiP overlay overlap, motion blur, scroll
 *   transition), we return `null` and let the session mark the frame
 *   `variant_unstable` rather than guess.
 *
 * Pure module: no DOM-specific imports. The frame pipeline passes the band's
 * raw RGBA buffer + width/height; the classifier returns the resolved
 * variant + confidence + source signals for diagnostics.
 */

import {
  colourFamilyVetoesVariant,
  type PokedexVariant,
} from "./pogoPokedexVariantClassifier";
import { dexScanVerboseLog } from "./dexScanLog";

// ─────────────────────────────────────────────────────────────────────────────
// Canonical pill order
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The fixed order of pills as Pokémon GO renders them in the dex bottom band.
 *
 * This is a *visual* order — it does not enumerate every dex variant Nexus
 * tracks (shundo/shinyLucky are manual-only and never appear as PoGo pills).
 * Mega/GMax/Shadow/Purified are listed because they ARE real PoGo pills, but
 * they never write through this scan path today (they are terminal in
 * CASCADE_GRAPH); we keep them in the order so neighbour pinning still works
 * when the user scrolls into them.
 */
export const PILL_ORDER: readonly PokedexVariant[] = [
  "pokemon",
  "shiny",
  "lucky",
  "xxl",
  "xxs",
  "gmax",
  "mega",
  "shadow",
  "purified",
  "perfect",
] as const;

/**
 * Slot positions of the active pill on screen (the bottom band shows 3
 * pills at a time; the active one sits in one of these slots).
 */
export type PillSlot = "left" | "middle" | "right";

/**
 * Token tables. Order is irrelevant here — we run all regexes against
 * EACH slot's isolated OCR text and pick the unique winner. No "first-match
 * wins" footgun.
 */
const PILL_TOKENS: Record<PokedexVariant, RegExp[]> = {
  pokemon: [/\bpok[eé]mon\b/i, /\bpokemon\b/i],
  shiny: [/\bshiny\b/i],
  lucky: [/\blucky\b/i],
  // 2026-08-30 (inflated-Perfect heal): the pill reads "★ 100%", so require
  // the % — a bare "100" is a dex number or progress figure bleeding into the
  // slot crop, and "3 star" never appears on this pill at all. Both used to
  // flip whole frames into the Perfect dex.
  perfect: [/\bperfect\b/i, /\b100\s*%/i],
  xxl: [/\bxxl\b/i],
  xxs: [/\bxxs\b/i],
  mega: [/\bmega\b/i],
  gmax: [/\bg[-\s]?max\b/i, /\bgigantamax\b/i],
  shadow: [/\bshadow\b/i],
  purified: [/\bpurified\b/i],
  // The manual-only + derived variants. They never appear as PoGo pills, but
  // the type system requires us to enumerate every variant key. Empty token
  // list means no slot will ever match them.
  complete: [],
  shundo: [],
  shinyLucky: [],
  dynamax: [],
  regional: [],
  costume: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Theme hue per variant. Used as a SANITY check, never as the sole signal.
// Each variant defines the [lo, hi] hue range (in degrees, 0..360) and a
// minimum saturation threshold the active pill should clear. `wraps360` means
// the range wraps over 360→0 (e.g. lucky's pink/orange straddles 0).
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeHueRange {
  lo: number; // degrees
  hi: number;
  minSat: number;
  wraps360?: boolean;
}

const THEME_HUE: Record<PokedexVariant, ThemeHueRange | null> = {
  pokemon: { lo: 180, hi: 220, minSat: 0.3 },   // cyan/teal blue
  shiny: { lo: 30, hi: 65, minSat: 0.4 },        // gold/yellow
  lucky: { lo: 320, hi: 50, minSat: 0.25, wraps360: true }, // pink/orange
  perfect: { lo: 0, hi: 360, minSat: 0.0 },     // near-grey/silver, any hue OK
  xxl: { lo: 150, hi: 210, minSat: 0.2 },        // mint/teal
  xxs: { lo: 150, hi: 210, minSat: 0.2 },        // mint/teal (shares with xxl)
  // 2026-08-30 audit: mega/gmax used {lo:0,hi:360,minSat:0} "skip check"
  // entries, which made hueAgreesWith TAUTOLOGICALLY TRUE — a free
  // hue-corroboration bump (0.90 -> 0.97) that carried a pinned mega/gmax
  // read past the 0.95 colour-veto-override bar every other variant must
  // genuinely earn. null means "no theme range known": hueAgreesWith returns
  // false, so the read keeps its honest 0.90 confidence.
  mega: null,
  gmax: null,
  shadow: { lo: 250, hi: 290, minSat: 0.2 },    // purple
  purified: { lo: 140, hi: 180, minSat: 0.15 }, // mint/seafoam green
  complete: null,
  shundo: null,
  shinyLucky: null,
  dynamax: null,
  regional: null,
  costume: null,
};

function inHueRange(hue: number, range: ThemeHueRange): boolean {
  const h = ((hue % 360) + 360) % 360;
  if (range.wraps360) {
    return h >= range.lo || h <= range.hi;
  }
  return h >= range.lo && h <= range.hi;
}

// ─────────────────────────────────────────────────────────────────────────────
// Band geometry: find the active pill within the bottom band.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of locating the active pill in the band.
 *   - x_center_frac: 0..1, horizontal centre of the detected active pill.
 *   - width_frac:    0..1, width of the pill (used to size neighbour slot
 *                    crops).
 *   - score:         0..1, peak detection confidence. Below ~0.12 we treat
 *                    the result as untrusted (likely PiP overlay or scroll
 *                    transition).
 *   - hue:           degrees, dominant hue at the peak column. null when
 *                    near-achromatic (matches perfect / silver theme).
 *   - saturation:    0..1, saturation at the peak column.
 */
export interface ActivePillLocation {
  xCenterFrac: number;
  widthFrac: number;
  score: number;
  hue: number | null;
  saturation: number;
}

/**
 * Our own in-app PiP status card renders a fixed green (~137°, on-device peaks
 * read 108–112). No PoGo variant pill is green, yet that overlay is BOTH more
 * saturated and much darker than the real pill, so it wins the column-scan peak
 * and the active pill is never resolved. Suppress columns whose dominant hue is
 * in this band from the peak search so the overlay can never masquerade as the
 * active pill. Kept TIGHT (95–135°) to stay clear of purified's seafoam
 * (140–180°), and chroma-gated so it only fires on genuinely chromatic green.
 */
export const PIP_SUPPRESS_HUE_LO = 95;
export const PIP_SUPPRESS_HUE_HI = 135;
export const PIP_SUPPRESS_MIN_CHROMA_FRAC = 0.25;

/**
 * Locate the active pill inside an RGBA buffer of the bottom-band crop.
 *
 *   - `bandData`:  RGBA pixel buffer (row-major) of the band (length =
 *                  4 * bandWidth * bandHeight).
 *   - `bandWidth`, `bandHeight`: pixel dimensions of the band buffer.
 *
 * The active pill is detected by scanning columns of the band:
 *   - Per-column mean saturation and mean value (HSV).
 *   - The active pill has notably HIGHER saturation than the cream/light
 *     background AND a LOWER value (darker fill) than the surrounding
 *     inactive pills. Score = saturation * (0.4 + 2.0 * max(0, medianValue
 *     - colValue)). The `+0.4` keeps the score positive even for
 *     identically-light pills (POKÉMON blue on a teal background is
 *     similarly bright but more saturated).
 *
 *   - Smooth via a fixed-width moving average (~4% of band width — the
 *     pills themselves are ~30% wide so a 4% smoother removes pixel
 *     speckle without blurring boundaries).
 *
 *   - The pill width is estimated by walking out from the peak until the
 *     score drops below 55% of peak.
 *
 * Pure function. Does not allocate canvases. Returns the location even
 * when score is low (caller decides the threshold).
 */
export function locateActivePill(
  bandData: Uint8ClampedArray | Uint8Array,
  bandWidth: number,
  bandHeight: number,
): ActivePillLocation {
  if (bandWidth <= 0 || bandHeight <= 0) {
    return { xCenterFrac: 0.5, widthFrac: 0, score: 0, hue: null, saturation: 0 };
  }

  // Per-column accumulators. We compute mean saturation, mean value, and
  // the circular mean of hue (as unit-vector components) for each column.
  const colSat = new Float32Array(bandWidth);
  const colVal = new Float32Array(bandWidth);
  const colHx = new Float32Array(bandWidth);
  const colHy = new Float32Array(bandWidth);
  const colChromaCount = new Uint32Array(bandWidth);

  for (let y = 0; y < bandHeight; y++) {
    for (let x = 0; x < bandWidth; x++) {
      const idx = (y * bandWidth + x) * 4;
      const r = bandData[idx] / 255;
      const g = bandData[idx + 1] / 255;
      const b = bandData[idx + 2] / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;
      const sat = max > 1e-6 ? delta / max : 0;
      colSat[x] += sat;
      colVal[x] += max;
      if (delta >= 0.06) {
        let hh: number;
        if (max === r) hh = ((g - b) / delta) % 6;
        else if (max === g) hh = (b - r) / delta + 2;
        else hh = (r - g) / delta + 4;
        const deg = hh * 60;
        const rad = (deg < 0 ? deg + 360 : deg) * (Math.PI / 180);
        colHx[x] += Math.cos(rad);
        colHy[x] += Math.sin(rad);
        colChromaCount[x] += 1;
      }
    }
  }

  for (let x = 0; x < bandWidth; x++) {
    colSat[x] /= bandHeight;
    colVal[x] /= bandHeight;
  }

  // Median column value (background brightness).
  const valSorted = Float32Array.from(colVal).sort();
  const medianVal = valSorted[Math.floor(valSorted.length / 2)];

  // Raw active-pill score per column.
  const score = new Float32Array(bandWidth);
  for (let x = 0; x < bandWidth; x++) {
    const drop = Math.max(0, medianVal - colVal[x]);
    score[x] = colSat[x] * (0.4 + 2.0 * drop);
  }

  // Suppress our own green PiP overlay BEFORE smoothing. A column that is
  // chromatic green (chroma fraction ≥ PIP_SUPPRESS_MIN_CHROMA_FRAC, dominant
  // hue within the tight PiP green window) has its raw score zeroed, so the
  // suppression propagates through the moving average naturally and the overlay
  // can never become the peak. Full-width scan is preserved. Same circular-mean
  // hue math as the peak-column read below.
  for (let x = 0; x < bandWidth; x++) {
    const chroma = colChromaCount[x];
    if (chroma === 0) continue;
    const chromaFrac = chroma / bandHeight;
    if (chromaFrac < PIP_SUPPRESS_MIN_CHROMA_FRAC) continue;
    const hx = colHx[x] / chroma;
    const hy = colHy[x] / chroma;
    const deg = Math.atan2(hy, hx) * (180 / Math.PI);
    const colHue = ((deg % 360) + 360) % 360;
    if (colHue >= PIP_SUPPRESS_HUE_LO && colHue <= PIP_SUPPRESS_HUE_HI) {
      score[x] = 0;
    }
  }

  // Smooth with a moving average of width ~4% of the band.
  const k = Math.max(8, Math.floor(bandWidth * 0.04));
  const smoothed = new Float32Array(bandWidth);
  let running = 0;
  for (let x = 0; x < bandWidth; x++) {
    running += score[x];
    if (x >= k) running -= score[x - k];
    smoothed[x] = running / Math.min(k, x + 1);
  }
  // The above is a left-biased causal average; centre it by shifting half-k
  // to the left. (Out-of-bounds reads clamp to edges.)
  const shifted = new Float32Array(bandWidth);
  const half = Math.floor(k / 2);
  for (let x = 0; x < bandWidth; x++) {
    const src = Math.min(bandWidth - 1, x + half);
    shifted[x] = smoothed[src];
  }

  // Find the smoothed peak.
  let peakX = 0;
  let peakScore = -Infinity;
  for (let x = 0; x < bandWidth; x++) {
    if (shifted[x] > peakScore) {
      peakScore = shifted[x];
      peakX = x;
    }
  }

  // Estimate pill width: walk left/right until score drops below 55% of peak.
  const thresh = peakScore * 0.55;
  let L = peakX;
  while (L > 0 && shifted[L - 1] >= thresh) L -= 1;
  let R = peakX;
  while (R < bandWidth - 1 && shifted[R + 1] >= thresh) R += 1;
  const widthPx = R - L;
  const centerPx = (L + R) / 2;

  // Hue/saturation at the peak column (use circular mean over the column).
  let hue: number | null = null;
  const chroma = colChromaCount[peakX];
  if (chroma > 0) {
    const hx = colHx[peakX] / chroma;
    const hy = colHy[peakX] / chroma;
    const deg = Math.atan2(hy, hx) * (180 / Math.PI);
    hue = ((deg % 360) + 360) % 360;
  }
  const saturation = colSat[peakX];

  return {
    xCenterFrac: centerPx / bandWidth,
    widthFrac: widthPx / bandWidth,
    score: peakScore,
    hue,
    saturation,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Slot resolution: which slot (left/middle/right) is the active pill in?
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decide which on-screen slot holds the active pill, based on its centre x.
 *
 * The band shows ~3 pills. Slot boundaries are 1/3 ticks:
 *   - x_center < 0.33  → left
 *   - x_center > 0.66  → right
 *   - otherwise        → middle
 *
 * Slight asymmetry handling: when the active pill sits at the rightmost
 * scroll position (PURIFIED), its centre can read >0.9 because the strip
 * stops aligned to the right edge with only 1 or 2 partial neighbours
 * visible. Same on the left for POKÉMON. The 1/3 boundary still picks the
 * right slot correctly.
 */
export function pillSlotFromCenter(xCenterFrac: number): PillSlot {
  if (xCenterFrac < 0.33) return "left";
  if (xCenterFrac > 0.66) return "right";
  return "middle";
}

// ─────────────────────────────────────────────────────────────────────────────
// Variant pinning from slot OCR + canonical order.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-slot OCR results. Each text is what the caller OCR'd from the slot
 * crop (a horizontal sub-band). At least the active slot's text is needed;
 * neighbours strengthen the pin but a single confident text can win on its
 * own.
 */
export interface SlotOcrTexts {
  left: string;
  middle: string;
  right: string;
}

/**
 * Strip fused dex-number digit runs off alpha pill tokens so the
 * word-boundary–anchored regexes in {@link PILL_TOKENS} can match.
 *
 * WHY: on device the active pill text is frequently read WITH the adjacent
 * dex-number digits fused onto the token — e.g. "G-MAX470" or "1G-MAX" (the
 * dex number bleeds into the pill crop). The token patterns anchor on `\b`
 * (e.g. `\bg[-\s]?max\b`). A fused digit run leaves NO word boundary between
 * "MAX" and "470" (or "1" and "G"), so the anchored regex never matches and
 * gmax (and any other digit-fused variant) resolves to null — the confirmed
 * cause of the gmax detection blackout.
 *
 * This normalises the TEXT ONLY (the regexes keep their `\b` anchors intact,
 * so token-boundary strictness is preserved and no new false matches are
 * introduced). It removes a digit run ONLY where it is directly fused to a
 * letter — either digits immediately followed by a letter ("1G-MAX",
 * "470MEGA") or a letter immediately followed by digits ("G-MAX470",
 * "MEGA470"). Standalone digit groups that legitimately form a token (e.g.
 * PERFECT's "100%", "3-star") are NOT adjacent to a letter and are left
 * untouched. Variant-agnostic by construction.
 */
export function normalizeSlotTextForTokens(text: string): string {
  return text
    // digits fused to the START of an alpha token: "1G-MAX" -> " G-MAX"
    .replace(/\d+(?=[A-Za-z])/g, " ")
    // digits fused to the END of an alpha token: "G-MAX470" -> "G-MAX "
    .replace(/([A-Za-z])\d+/g, "$1 ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Match a slot's OCR text against every variant's tokens. Returns the
 * unique variant whose token matched. If multiple variants match (rare, e.g.
 * an OCR run that bled the neighbour text into the slot crop), returns
 * `null` — the caller should not guess.
 *
 * The text is first passed through {@link normalizeSlotTextForTokens} so a
 * dex-number-fused token like "G-MAX470" still matches the anchored pattern.
 */
function matchTokens(text: string): PokedexVariant[] {
  const hits: PokedexVariant[] = [];
  for (const variant of Object.keys(PILL_TOKENS) as PokedexVariant[]) {
    for (const re of PILL_TOKENS[variant]) {
      if (re.test(text)) {
        hits.push(variant);
        break;
      }
    }
  }
  return hits;
}

/**
 * Letters — not digits — that OCR fuses onto a pill token, and how many of them
 * we will shave off a word's edges before giving up.
 *
 * {@link normalizeSlotTextForTokens} strips fused DIGIT runs, which was the
 * whole story while the noise was bleeding dex numbers. The 2026-07-23 device
 * log shows the other half: the pill's own size/scale ICON glyph OCRs as a
 * letter and fuses to the token, which defeats the `\b` anchor exactly the same
 * way. 179 of 1256 slot reads (14%) resolved to null on nothing but this —
 * `XXLU`, `XXSU`, `TXXS`, `XXSJU`, `OUXXL`, `MEGAU`, `LUCKYUU`, `PURIFIEDJ`.
 *
 * It is not evenly distributed, which is why XXL felt so much worse than XXS on
 * that scan: `01 QXXL010` and `XXLU` failed while `1XXS` and `XXS64` (digit
 * noise only) sailed through. The dex the user could not get to apply was the
 * one whose icon happened to OCR as a letter.
 *
 * Two is deliberate: it covers one glyph plus one stray, and it is short enough
 * that no word in the vocabulary can be trimmed into a DIFFERENT variant's
 * token. `REGIONSG-MAXS` stays unmatched at this budget, which is correct — it
 * is a page header bleeding in, not a pill.
 */
const MAX_EDGE_GLYPH_LETTERS = 2;

/** Shortest real token core (`XXL`, `XXS`); never trim below it. */
const MIN_TOKEN_CORE = 3;

/**
 * Every way of shaving up to {@link MAX_EDGE_GLYPH_LETTERS} letters off each end
 * of a word. Interior noise is deliberately NOT handled — a token with garbage
 * inside it is not a confident read, and reaching into the middle is how a
 * matcher starts inventing tokens that were never on screen.
 */
function edgeTrimCandidates(word: string): string[] {
  const out: string[] = [];
  for (let lead = 0; lead <= MAX_EDGE_GLYPH_LETTERS; lead++) {
    for (let trail = 0; trail <= MAX_EDGE_GLYPH_LETTERS; trail++) {
      if (lead === 0 && trail === 0) continue;
      const end = word.length - trail;
      if (end - lead < MIN_TOKEN_CORE) continue;
      out.push(word.slice(lead, end));
    }
  }
  return out;
}

export function variantFromSlotText(text: string): PokedexVariant | null {
  if (!text) return null;
  const normalized = normalizeSlotTextForTokens(text);
  if (!normalized) return null;
  let hits = matchTokens(normalized);
  let edgeTrimmed = false;
  if (hits.length === 0) {
    // Nothing matched cleanly. Retry per word with the icon-glyph allowance.
    // Runs ONLY on a strict miss, so every read that resolves today resolves
    // identically — this can add a match, never change one.
    const found = new Set<PokedexVariant>();
    for (const word of normalized.split(/\s+/)) {
      for (const candidate of edgeTrimCandidates(word)) {
        for (const v of matchTokens(candidate)) found.add(v);
      }
    }
    if (found.size > 0) {
      hits = [...found];
      edgeTrimmed = true;
    }
  }
  const matched = hits.length === 1 ? hits[0] : null;
  if (edgeTrimmed) {
    dexScanVerboseLog("variant slot-text edge-trimmed", () => ({
      rawText: text,
      normalizedText: normalized,
      matchedVariant: matched,
      hitCount: hits.length,
    }));
    return matched;
  }
  if (normalized !== text) {
    dexScanVerboseLog("variant slot-text normalized", () => ({
      rawText: text,
      normalizedText: normalized,
      matchedVariant: matched,
      hitCount: hits.length,
    }));
  }
  return matched;
}

/**
 * Pin the active variant by walking the canonical PILL_ORDER and matching
 * the slot triple.
 *
 * Algorithm: for every consecutive triple (and edge cases for the left/right
 * extremes), check whether the slot texts agree. The active slot wins
 * outright when we have a confident token match for it. When the active
 * slot is uncertain (e.g. PiP partially obscures it), we use the neighbours
 * to disambiguate.
 *
 *   Example: slots = { left: "POKEMON", middle: "SHINY", right: "LUCKY" },
 *            activeSlot = "left"
 *     → matches order index 0 (POKEMON) with right neighbours SHINY (1) and
 *       LUCKY (2). Active = POKEMON. Confident.
 *
 *   Example: slots = { left: "", middle: "", right: "LUCKY" },
 *            activeSlot = "right"
 *     → active text matches LUCKY directly. Active = LUCKY. Confident.
 *
 *   Example: slots = { left: "POKEMON", middle: "SHINY", right: "LUCKY" },
 *            activeSlot = "middle"
 *     → active text matches SHINY directly AND POKEMON/LUCKY confirm
 *       SHINY's order neighbours. Active = SHINY. High confidence.
 *
 * Returns null when no consistent assignment exists.
 *
 * @internal exported for tests
 */
export function pinActiveVariantFromSlots(
  slots: SlotOcrTexts,
  activeSlot: PillSlot,
): { variant: PokedexVariant; agreedNeighbours: number } | null {
  const leftV = variantFromSlotText(slots.left);
  const middleV = variantFromSlotText(slots.middle);
  const rightV = variantFromSlotText(slots.right);

  // 1. Strong path: the active slot itself OCR'd a single token cleanly.
  const activeV = activeSlot === "left" ? leftV : activeSlot === "right" ? rightV : middleV;
  if (activeV) {
    // Verify position constraints from the canonical order.
    const idx = PILL_ORDER.indexOf(activeV);
    if (idx < 0) return null;
    // POKEMON (index 0) can only be on the LEFT or MIDDLE (scrolled max-left).
    if (activeV === "pokemon" && activeSlot === "right") return null;
    // PERFECT (last index, rendered as "100%") can only be on MIDDLE or RIGHT
    // (scroll stops at PERFECT on the right edge).
    if (activeV === "perfect" && activeSlot === "left") return null;
    // PURIFIED (second-to-last) can be MIDDLE or RIGHT in normal scroll —
    // [PURIFIED, PERFECT, ?] is not a valid triple (nothing after PERFECT),
    // so PURIFIED never sits in the LEFT slot of a populated triple.
    if (activeV === "purified" && activeSlot === "left") return null;

    // XXL ⇄ XXS ARBITRATION (2026-07-23). A direct read of the active pill
    // normally wins outright, which is right for every token that is a whole
    // distinct word. It is wrong for this pair: one glyph separates them, they
    // share a hue so the colour guard below is blind, and the device log shows
    // the HUD flipping between them across a single dwell.
    //
    // The neighbours are the stronger evidence here, and they are exactly the
    // context the user described: LUCKY on the left means the active pill is
    // XXL; XXL on the left (or G-MAX on the right) means it is XXS. Those are
    // long, unambiguous words — no single-character discrimination anywhere in
    // the chain. So when a neighbour pin contradicts an xxl/xxs read by naming
    // its SIBLING, the neighbours win.
    //
    // Deliberately narrow: only for the xxl/xxs pair, and only when the
    // neighbours name the sibling specifically. A neighbour that disagrees in
    // any other way is a genuine conflict, not an L-vs-S misread, and is left
    // to the existing precedence guards rather than silently overridden.
    const sibling = PILL_SIBLING[activeV];
    if (sibling) {
      const impliedV = inferFromNeighbours(leftV, middleV, rightV, activeSlot);
      if (impliedV === sibling) {
        dexScanVerboseLog("variant pill sibling arbitration", () => ({
          activeSlot,
          readOnPill: activeV,
          impliedByNeighbours: impliedV,
          left: leftV,
          middle: middleV,
          right: rightV,
        }));
        return { variant: sibling, agreedNeighbours: 1 };
      }
    }

    // Count neighbour agreement.
    let agreed = 0;
    if (activeSlot === "left") {
      // Right neighbours should be PILL_ORDER[idx+1] and PILL_ORDER[idx+2].
      if (idx + 1 < PILL_ORDER.length && middleV === PILL_ORDER[idx + 1]) agreed += 1;
      if (idx + 2 < PILL_ORDER.length && rightV === PILL_ORDER[idx + 2]) agreed += 1;
    } else if (activeSlot === "right") {
      if (idx - 1 >= 0 && middleV === PILL_ORDER[idx - 1]) agreed += 1;
      if (idx - 2 >= 0 && leftV === PILL_ORDER[idx - 2]) agreed += 1;
    } else {
      if (idx - 1 >= 0 && leftV === PILL_ORDER[idx - 1]) agreed += 1;
      if (idx + 1 < PILL_ORDER.length && rightV === PILL_ORDER[idx + 1]) agreed += 1;
    }
    return { variant: activeV, agreedNeighbours: agreed };
  }

  const inferred = inferFromNeighbours(leftV, middleV, rightV, activeSlot);
  if (inferred) return { variant: inferred, agreedNeighbours: 1 };
  return null;
}

/**
 * The one pair in the vocabulary that a single OCR character decides.
 *
 * Every other token is a whole distinct word. XXL and XXS differ by one glyph,
 * AND they share a theme hue (`THEME_HUE.xxl === THEME_HUE.xxs`, both mint/teal),
 * so the colour arbitration that separates every other confusable pair is
 * structurally blind here. Text is the only signal, and it is the weakest one
 * we have for exactly this pair.
 */
const PILL_SIBLING: Partial<Record<PokedexVariant, PokedexVariant>> = {
  xxl: "xxs",
  xxs: "xxl",
};

/**
 * Infer the active slot's variant purely from its neighbours' positions in
 * PILL_ORDER. Needs at least one confident neighbour token.
 */
function inferFromNeighbours(
  leftV: PokedexVariant | null,
  middleV: PokedexVariant | null,
  rightV: PokedexVariant | null,
  activeSlot: PillSlot,
): PokedexVariant | null {
  if (activeSlot === "left") {
    // Active is left. Middle is PILL_ORDER[idx+1] or right is PILL_ORDER[idx+2].
    if (middleV) {
      const j = PILL_ORDER.indexOf(middleV);
      if (j > 0) return PILL_ORDER[j - 1];
    }
    if (rightV) {
      const j = PILL_ORDER.indexOf(rightV);
      if (j > 1) return PILL_ORDER[j - 2];
    }
  } else if (activeSlot === "right") {
    if (middleV) {
      const j = PILL_ORDER.indexOf(middleV);
      if (j >= 0 && j + 1 < PILL_ORDER.length) {
        return PILL_ORDER[j + 1];
      }
    }
    if (leftV) {
      const j = PILL_ORDER.indexOf(leftV);
      if (j >= 0 && j + 2 < PILL_ORDER.length) {
        return PILL_ORDER[j + 2];
      }
    }
  } else {
    // middle
    if (leftV) {
      const j = PILL_ORDER.indexOf(leftV);
      if (j + 1 < PILL_ORDER.length) {
        return PILL_ORDER[j + 1];
      }
    }
    if (rightV) {
      const j = PILL_ORDER.indexOf(rightV);
      if (j > 0) return PILL_ORDER[j - 1];
    }
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level classifier
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Source signal for the final decision. Mirrors the legacy VariantChipSignal
 * shape so downstream consumers (dexLiveFrameAdapter routing) need only the
 * smallest delta.
 */
export type ActivePillSource =
  | "active-pill-text-strong"     // active slot text + neighbour agreement + theme hue agree
  | "active-pill-text"            // active slot text alone (theme hue not checked or null)
  | "active-pill-neighbours"      // active slot text unreadable, inferred from neighbours
  | "hue-fallback"                // no text at all; theme hue at peak resolved a single variant
  | "none";

export interface ActivePillClassification {
  source: ActivePillSource;
  variant: PokedexVariant | null;
  confidence: number;
  /** Diagnostic: where the active pill sat on screen. */
  slot: PillSlot | null;
  /** Diagnostic: peak detection score. */
  peakScore: number;
  /** Diagnostic: theme hue at the peak, in degrees, when chromatic. */
  peakHue: number | null;
  /** Diagnostic: theme hue agrees with the resolved variant. */
  hueAgrees: boolean;
  /**
   * Diagnostic (telemetry): set when a directly-read chip-text token DISAGREED
   * with the geometry/neighbour pin and a precedence guard re-routed (or would
   * have re-routed) the result. Lets a device trace see WHY a G-MAX frame was
   * accepted as gmax instead of the xxs the slot geometry pinned. Undefined when
   * no conflict arose.
   */
  conflict?: {
    /** The variant a raw on-screen chip token NAMED. */
    rawText: PokedexVariant;
    /** The variant the geometry/neighbour pin resolved before the guard. */
    pinned: PokedexVariant;
    /** What the guard did: "promoted" raw over pin, or "rejected" the frame. */
    resolution: "promoted" | "rejected";
  };
}

/**
 * Minimum peak score for the active-pill location to be trusted.
 * Empirically the warm-theme screens (Shiny/Lucky/Perfect) score ~0.18-0.30
 * and the cool-theme screens (Pokémon/XXL/Shadow) score ~0.30-0.45.
 * 0.12 is comfortably below both; below it the band is usually obscured by
 * the PiP overlay or in a scroll transition.
 */
export const MIN_PEAK_SCORE = 0.12;

/**
 * Classify the active pill given a located peak + per-slot OCR texts.
 *
 *   - `location`:  the result of `locateActivePill`.
 *   - `slots`:     per-slot OCR text triple. Each string may be empty if the
 *                  caller skipped that slot (e.g. the active slot is at the
 *                  left edge and there is no left-of-active slot).
 *
 * Returns `{ variant: null, source: "none" }` when no confident assignment
 * exists. The caller (dexLiveFrameAdapter) should treat null as "skip this
 * frame" rather than defaulting to any variant.
 */
export function classifyActivePill(
  location: ActivePillLocation,
  slots: SlotOcrTexts,
  /**
   * Optional page-colour family (from the pre-OCR gate). When supplied it acts
   * as a VETO only: a precedence guard may not PROMOTE a variant the page colour
   * forbids. This stops stale overlay/chip text (e.g. a leftover `G-MAX` PiP read
   * after the page has tinted cyan for an XXS scan) from overriding the
   * colour-consistent pin. Colour never positively SELECTS here — it only blocks
   * an impossible promotion, leaving the original pin to stand.
   */
  pageColourFamily?: string | null,
): ActivePillClassification {
  // Untrusted geometry → skip.
  if (location.score < MIN_PEAK_SCORE) {
    return {
      source: "none",
      variant: null,
      confidence: 0,
      slot: null,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: false,
    };
  }

  const slot = pillSlotFromCenter(location.xCenterFrac);
  const pinned = pinActiveVariantFromSlots(slots, slot);

  // Theme hue check (when we have a hue + a hue range for the variant).
  // Hoisted ABOVE the Perfect-vs-Purified guard so that guard can consult the
  // physical on-screen pill colour to break the tie (see below).
  function hueAgreesWith(variant: PokedexVariant): boolean {
    const range = THEME_HUE[variant];
    if (!range) return false;
    // Perfect is intentionally low-sat — saturation alone vetoes here.
    if (variant === "perfect") return location.saturation < 0.22;
    if (location.hue == null) return range.minSat <= 0; // achromatic peak
    if (location.saturation < range.minSat) return false;
    return inHueRange(location.hue, range);
  }

  // PR-E22 #1 - Perfect-vs-Purified disambiguation (REVISED 2026-06-28).
  //
  // Perfect ("100%") is the TERMINAL pill in PILL_ORDER, sitting IMMEDIATELY
  // AFTER Purified, so on BOTH the Perfect dex AND the Purified dex the visible
  // triple is `[SHADOW, PURIFIED, 100%]` — a "100%"/PERFECT token is ALWAYS on
  // screen next to the active Purified pill. The original guard promoted Perfect
  // whenever that token was present and geometry pinned Purified; that fired on
  // EVERY genuine Purified scan and wrote 37 Perfect rows for a Purified dex
  // (device telemetry 2026-06-28: slot=middle, peakHue 140-146 = mint, the
  // PHYSICAL Purified pill colour, yet variant flipped purified→perfect).
  //
  // Authority principle: the ONLY thing that matters is the variant shown on the
  // PoGo Pokédex screen — never app_state. The active pill's on-screen COLOUR is
  // that ground truth. Perfect's active pill renders near-grey / low-saturation;
  // Purified's renders saturated mint/seafoam (~140-180 deg). So:
  //
  //   - When geometry peaked on a SATURATED MINT pill (hueAgreesWith("purified")
  //     is true), the on-screen active pill IS Purified — keep the Purified pin
  //     even though a "100%" token sits in the neighbour slot.
  //   - Only when the peak is NOT mint-saturated (i.e. the active pill is the
  //     grey Perfect capsule and geometry merely under-scored it onto the
  //     Purified neighbour) do we promote Perfect.
  //
  // This keeps the genuine-Perfect recovery the PR-E22 fix was built for (grey
  // active pill, geometry slips onto the saturated Purified neighbour) while no
  // longer hijacking genuine Purified scans (mint active pill).
  // TERMINAL-PILL PERFECT (2026-09-24 device log). 100% is the LAST pill, so
  // when it is selected Pokémon GO centres it with EMPTY space to its right
  // ([PURIFIED][★ 100%][ ]). The locator can peak on that empty (saturated
  // coral) page area, putting the "active" slot right of PERFECT where no
  // pill can exist, and inferFromNeighbours has nothing after PERFECT to
  // infer, so every settled frame held at source "none". A 100% token read in
  // the CENTRED (middle) slot with no pill token to its right can only be the
  // selected Perfect pill: on the Purified dex the centre reads PURIFIED and
  // 100% sits in the right slot. Colour still vetoes (a mint page never
  // promotes).
  if (
    (!pinned || pinned.variant !== "perfect") &&
    variantFromSlotText(slots.middle) === "perfect" &&
    variantFromSlotText(slots.right) == null &&
    !colourFamilyVetoesVariant(pageColourFamily, "perfect")
  ) {
    const leftAgrees = variantFromSlotText(slots.left) === "purified";
    return {
      source: leftAgrees ? "active-pill-text-strong" : "active-pill-text",
      variant: "perfect",
      confidence: leftAgrees ? 0.9 : 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("perfect"),
    };
  }

  const perfectTokenOnScreen =
    variantFromSlotText(slots.left) === "perfect" ||
    variantFromSlotText(slots.middle) === "perfect" ||
    variantFromSlotText(slots.right) === "perfect";
  // 2026-08-30 tightening: promotion also requires the POSITIVE Perfect
  // signature (the near-grey capsule, saturation < 0.22), not merely "not
  // mint". A washed-out saturation read that fails the purified check without
  // showing the grey capsule now keeps the Purified pin instead of promoting.
  if (
    perfectTokenOnScreen &&
    pinned &&
    pinned.variant === "purified" &&
    !hueAgreesWith("purified") &&
    hueAgreesWith("perfect") &&
    // Parity with the GMAX-vs-XXS promotion below (2026-08-30 audit): a page
    // whose colour family cannot host Perfect must never promote to it, no
    // matter what a neighbour token says.
    !colourFamilyVetoesVariant(pageColourFamily, "perfect")
  ) {
    return {
      source: "active-pill-text",
      variant: "perfect",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: location.saturation < 0.22,
    };
  }

  // PURIFIED-vs-SHADOW disambiguation (telemetry 2026-06-22, mid-scan switch).
  //
  // SHADOW (idx 7) sits IMMEDIATELY BEFORE PURIFIED (idx 8) in PILL_ORDER, so on
  // the Purified dex the visible triple is `[…, SHADOW, PURIFIED, …]`. SHADOW's
  // pill is a dark, highly-saturated purple chip; PURIFIED's is a paler seafoam.
  // The saturation·value-drop peak therefore systematically over-scores the
  // SHADOW neighbour and pins the active slot onto SHADOW, with the PURIFIED
  // neighbour merely "agreeing" its order position — exactly the symmetric error
  // the Perfect-vs-Purified guard above corrects. The device trace shows it
  // outright: right after `TO JS {"text":"PURIFIED"}` the resolver logged
  // `variant:"shadow" confidence:0.9 hueAgrees:false` while the user was on the
  // Purified tab, and that bogus shadow read (impossible on the dex_pink page)
  // deadlocked every frame into variant_hold so the Purified dex never acquired.
  //
  // Fix: the active-pill TEXT is authoritative. When a PURIFIED token is present
  // on screen AND the geometry pinned its left-neighbour SHADOW AND the peak hue
  // does NOT look like SHADOW's purple theme (the tell that the peak landed on a
  // non-shadow pill), the SHADOW pin is a geometry misread — promote PURIFIED.
  // The hue guard keeps a GENUINE shadow scan (purple peak, purified merely
  // visible on the next tab) intact: a real shadow pill DOES match its purple
  // range, so the guard does not fire and shadow is preserved.
  const purifiedTokenOnScreen =
    variantFromSlotText(slots.left) === "purified" ||
    variantFromSlotText(slots.middle) === "purified" ||
    variantFromSlotText(slots.right) === "purified";
  if (
    purifiedTokenOnScreen &&
    pinned &&
    pinned.variant === "shadow" &&
    !hueAgreesWith("shadow")
  ) {
    return {
      source: "active-pill-text",
      variant: "purified",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("purified"),
    };
  }

  // SHADOW-vs-MEGA disambiguation (telemetry 2026-06-22, focused #476 trace).
  //
  // MEGA (idx 6) sits IMMEDIATELY BEFORE SHADOW (idx 7) in PILL_ORDER, so on the
  // Shadow dex the visible triple is `[…, MEGA, SHADOW, …]`. MEGA's pill out-scores
  // a paler/blurred SHADOW active pill, so the saturation·value-drop peak centres
  // the active SLOT on MEGA; when the SHADOW slot itself is unreadable the pinner
  // then INFERS the active variant as shadow's left-neighbour — MEGA — purely from
  // the clean SHADOW token next door, even though SHADOW is the tab the user
  // selected. The device trace shows it outright: right after `TO JS {"text":
  // "SHADOW"}` the resolver logged `variant:"mega" confidence:0.97 hueAgrees:true`
  // (mega's theme range is wide-open so the yellow/orange peak "agreed") and the
  // bogus mega read deadlocked the Shadow dex.
  //
  // Fix: a directly-read SHADOW token is authoritative over a MEGA pin that the
  // active slot did NOT itself read. When any slot OCRs a standalone SHADOW token
  // AND the pin resolved MEGA AND the active slot's own text is NOT mega (i.e. mega
  // was INFERRED from the shadow neighbour, not read on the pill), promote SHADOW —
  // regardless of the (open-range) mega peak hue. A GENUINE mega scan reads MEGA
  // directly on the active pill, so `activeReadsMega` is true and the guard does
  // not fire: MEGA text still resolves to mega.
  const activeSlotText =
    slot === "left" ? slots.left : slot === "right" ? slots.right : slots.middle;
  const shadowTokenOnScreen =
    variantFromSlotText(slots.left) === "shadow" ||
    variantFromSlotText(slots.middle) === "shadow" ||
    variantFromSlotText(slots.right) === "shadow";
  const activeReadsMega = variantFromSlotText(activeSlotText) === "mega";
  if (
    shadowTokenOnScreen &&
    pinned &&
    pinned.variant === "mega" &&
    !activeReadsMega
  ) {
    return {
      source: "active-pill-text",
      variant: "shadow",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("shadow"),
    };
  }

  // GMAX-vs-XXS disambiguation (telemetry 2026-06-22, multi-dex #477 trace).
  //
  // GMAX (idx 5) sits IMMEDIATELY AFTER XXS (idx 4) in PILL_ORDER, so on the
  // G-Max dex the visible triple is `[XXS, GMAX, …]` or `[XXL, XXS, GMAX]`.
  // XXS's pill out-scores a paler/blurred GMAX active pill, so the
  // saturation·value-drop peak centres the active SLOT on XXS while GMAX merely
  // sits as the agreeing right-neighbour — pinning xxs with neighbour agreement
  // even though GMAX is the tab the user selected. The device trace shows it
  // outright: a frame whose slot OCR read a clean `G-MAX` resolved
  // `variant:"xxs" confidence:0.9 activePillSource:"active-pill-text-strong"
  // slot:"middle" peakHue:340 hueAgrees:false`, and that bogus xxs read voted /
  // locked the scan-context switch to xxs so the whole Kanto G-Max page routed
  // as XXS and committed zero G-Max rows.
  //
  // Fix: a directly-read G-MAX token is authoritative over an XXS pin whose own
  // peak hue does NOT look like XXS's mint/teal theme. The hue guard is the
  // tell: a GENUINE XXS scan tints mint/teal (~150–210°) so the peak agrees
  // with xxs's range and the guard stays inert; a G-Max page reads pink/other
  // (peakHue 340 on the trace) so `hueAgreesWith("xxs")` is false and the xxs
  // pin is exposed as a geometry misread on a non-xxs page → promote gmax.
  // Raw text precedence beats slot/hue inference, exactly as the brief requires.
  //
  // Issue-2 guard (telemetry 2026-06-22, cyan-XXS #479 trace): a STALE G-MAX
  // overlay/PiP read can linger one or more frames after the page has already
  // tinted cyan for an XXS scan. On that cyan page the trace logged
  // `variant classifier conflict {rawText:"gmax", pinned:"xxs", resolution:
  // "promoted"}` → `variant override applied {from:"xxs", to:"gmax"}` even
  // though appState=XXS and page colour=cyan, which then voted the scan-context
  // switch onto gmax and dropped real XXS rows. G-MAX is colour-IMPOSSIBLE on a
  // dex_cyan page (COLOUR_FAMILY_ALLOWED_VARIANTS.dex_cyan = pokemon/xxl/xxs),
  // so when the page colour is known we must NOT let a gmax token promote over a
  // colour-consistent xxs pin. The veto only suppresses the promotion; the
  // original xxs pin stands and flows through the normal path below. A GENUINE
  // G-Max scan tints dex_pink (gmax allowed) so the veto is inert there and the
  // PR #478 fix is preserved.
  const gmaxTokenOnScreen =
    variantFromSlotText(slots.left) === "gmax" ||
    variantFromSlotText(slots.middle) === "gmax" ||
    variantFromSlotText(slots.right) === "gmax";
  if (
    gmaxTokenOnScreen &&
    pinned &&
    pinned.variant === "xxs" &&
    !hueAgreesWith("xxs") &&
    !colourFamilyVetoesVariant(pageColourFamily, "gmax")
  ) {
    return {
      source: "active-pill-text",
      variant: "gmax",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("gmax"),
      conflict: { rawText: "gmax", pinned: "xxs", resolution: "promoted" },
    };
  }

  // GMAX-vs-MEGA disambiguation (device telemetry 2026-08-31: a Kanto G-Max
  // scan committed ZERO gmax rows while `variant override applied {from:
  // "pokemon", to:"mega", source:"chip-text", confidence:0.9}` fired 55x).
  //
  // MEGA (idx 6) is GMAX's (idx 5) immediate right-neighbour, so on the G-Max
  // dex the visible triple is `[XXS, G-MAX, MEGA]`. Neither variant has a
  // THEME_HUE (both pages tint the same pink), so hue can never arbitrate
  // this pair, and the selected G-MAX capsule is white-on-pink (poor OCR,
  // often fused with the floating REGIONS label) while the unselected MEGA
  // pill is dark-on-white and reads cleanly. The saturation peak then drifts
  // one slot right onto MEGA and the pin resolves mega — sometimes by READING
  // mega on the mis-pinned slot (so the shadow-guard's "was it inferred?"
  // test would not fire), sometimes by inferring it from the clean G-MAX
  // middle token.
  //
  // The reliable tell is Pokémon GO's own layout: the SELECTED filter pill is
  // always CENTRED in the strip (both gmax and mega sit mid-list, so the
  // edge-of-list exception never applies to this pair). A G-MAX token read in
  // the MIDDLE slot therefore IS the selected pill, and a mega pin on a side
  // slot is a geometry drift — promote gmax. On a genuine Mega scan the
  // middle slot reads MEGA (G-MAX sits to its left), so the guard is inert.
  // Same colour-veto discipline as the xxs guard above: a stale gmax read on
  // a page whose colour family excludes gmax never promotes.
  const middleReadsGmax = variantFromSlotText(slots.middle) === "gmax";
  if (
    middleReadsGmax &&
    pinned &&
    pinned.variant === "mega" &&
    slot !== "middle" &&
    !colourFamilyVetoesVariant(pageColourFamily, "gmax")
  ) {
    return {
      source: "active-pill-text",
      variant: "gmax",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("gmax"),
      conflict: { rawText: "gmax", pinned: "mega", resolution: "promoted" },
    };
  }

  // XXS-vs-XXL disambiguation (telemetry 2026-06-22, XXS-only #479 trace).
  //
  // XXL (idx 3) sits IMMEDIATELY BEFORE XXS (idx 4) in PILL_ORDER and the two
  // share an identical mint/teal theme hue (THEME_HUE.xxl === THEME_HUE.xxs), so
  // hue can never disambiguate them — only the directly-read token can. On the
  // XXS dex the visible triple is `[XXL, XXS, GMAX]`; when the active (XXS) pill
  // text is unreadable but the XXS neighbour OCRs cleanly, the pinner INFERS the
  // active variant as XXS's left-neighbour XXL purely from order. The device
  // trace shows it outright: a frame whose slot OCR read a clean `XXS` resolved
  // `variant:"xxl" activePillSource:"active-pill-text-strong" slot:"middle"
  // peakHue:45 hueAgrees:false`, and that bogus xxl read voted/locked the
  // scan-context switch to xxl so an XXS-only page committed XXL rows.
  //
  // Fix: a directly-read XXS token is authoritative over an XXL pin that the
  // active slot did NOT itself read. When any slot OCRs a standalone XXS token
  // AND the pin resolved XXL AND the active slot's own text is NOT xxl (i.e. xxl
  // was INFERRED from the xxs neighbour, not read on the pill), promote XXS.
  // A GENUINE XXL scan reads XXL directly on its active pill, so `activeReadsXxl`
  // is true and the guard does not fire: XXL text still resolves to xxl. Exact
  // XXS text therefore resolves to xxs, never xxl, per the brief.
  const xxsTokenOnScreen =
    variantFromSlotText(slots.left) === "xxs" ||
    variantFromSlotText(slots.middle) === "xxs" ||
    variantFromSlotText(slots.right) === "xxs";
  const activeReadsXxl = variantFromSlotText(activeSlotText) === "xxl";
  if (
    xxsTokenOnScreen &&
    pinned &&
    pinned.variant === "xxl" &&
    !activeReadsXxl
  ) {
    return {
      source: "active-pill-text",
      variant: "xxs",
      confidence: 0.85,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: hueAgreesWith("xxs"),
      conflict: { rawText: "xxs", pinned: "xxl", resolution: "promoted" },
    };
  }

  if (pinned) {
    const agrees = hueAgreesWith(pinned.variant);
    let source: ActivePillSource;
    let confidence: number;
    if (pinned.agreedNeighbours >= 1 && agrees) {
      source = "active-pill-text-strong";
      confidence = 0.97;
    } else if (pinned.agreedNeighbours >= 1) {
      source = "active-pill-text-strong";
      confidence = 0.9;
    } else if (agrees) {
      source = "active-pill-text";
      confidence = 0.85;
    } else {
      // Text alone, hue disagrees. Lower confidence so the apply gate can
      // require a second corroborating frame.
      source = "active-pill-text";
      confidence = 0.7;
    }
    return {
      source,
      variant: pinned.variant,
      confidence,
      slot,
      peakScore: location.score,
      peakHue: location.hue,
      hueAgrees: agrees,
    };
  }

  // Fix C: NO hue-only positive selection. The former "hue-fallback" path
  // (source:"hue-fallback", confidence 0.55) positively named a variant from
  // the theme hue alone when every slot OCR returned empty. On device it
  // produced source:"hue",variant:"shiny"/"purified" — exactly the colour-
  // guessing PR #446 removed, re-introduced one layer down. Colour may only act
  // as a VETO / corroboration inside the unified resolver, never as a positive
  // selector. So when text could not pin a variant we return no-variant ("none")
  // and let the resolver HOLD. The peak hue is still surfaced in `peakHue` for
  // downstream page-colour-family DETECTION (the veto), which is unchanged — we
  // only removed variant SELECTION from hue.
  return {
    source: "none",
    variant: null,
    confidence: 0,
    slot,
    peakScore: location.score,
    peakHue: location.hue,
    hueAgrees: false,
  };
}
